import { isAxiosError } from 'axios';
import type { SupportDoc } from '../../components/TenderDetails/types';
import { getLoadingKey } from '../../components/TenderDetails/utils';
import { generateAdHocDocument } from '../../services/api';
import type { User } from '../../types/auth';
import type { Tender } from '../../types/tender';
import { logDocumentActivity } from '../../utils/activityLogger';

export interface UseSupportDocsGenerationProps {
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  hasSections: boolean | undefined;
  setLoadingStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setTenderForms: React.Dispatch<React.SetStateAction<SupportDoc[]>>;
  refreshUserDetails: (silent?: boolean) => void | Promise<void>;
  handleDownload: (
    gsURI: string,
    fileTypeIdentifier: string,
    defaultFileName: string
  ) => Promise<void>;
}

export const useSupportDocsGeneration = ({
  tender,
  account,
  showSnackbar,
  hasSections,
  setLoadingStates,
  setTenderForms,
  refreshUserDetails,
  handleDownload,
}: UseSupportDocsGenerationProps) => {
  const handleGenerateAdHocDocument = async (
    docObj: SupportDoc,
    confidenceThreshold?: number,
    flagUncertainAnswers?: boolean
  ) => {
    if (!account?.uid || !account.clientId || !tender?.id || !docObj.originalFilename) {
      showSnackbar('Cannot generate document: Missing critical details.', 'error');
      return;
    }

    const hasDraftFile = !!tender.draftFileLink;

    if (!hasDraftFile && !hasSections) {
      showSnackbar('Please generate a draft proposal first.', 'warning');
      return;
    }

    try {
      const loadingKey = getLoadingKey(tender.id, 'generate', `form_${docObj.originalFilename}`);
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));

      setTenderForms((prev) =>
        prev.map((f) => {
          if (f.originalFilename === docObj.originalFilename) {
            const { filledVersionGcsPath, ...rest } = f as Record<string, unknown>;
            return {
              ...rest,
              generationStatus: 'initializing',
              generationProgress: 0,
              startedAt: new Date().toISOString(),
            } as SupportDoc;
          }
          return f;
        })
      );

      const jobId = crypto.randomUUID();
      await generateAdHocDocument({
        userId: account.uid,
        tenderId: tender.id,
        docFileName: docObj.filename || docObj.name || docObj.originalFilename,
        clientId: account.clientId,
        isStrictForm: docObj.isStrictForm,
        jobId,
        ...(confidenceThreshold !== undefined ? { confidenceThreshold } : {}),
        ...(flagUncertainAnswers !== undefined ? { flagUncertainAnswers } : {}),
      });

      showSnackbar('Document generation started. This may take a moment.', 'success');
      logDocumentActivity.regenerated({
        fileName: docObj.originalFilename,
        type: 'ad_hoc_document',
        source: `tender_details/${tender.id}`,
      });
      refreshUserDetails(true);
    } catch (error) {
      let message = error instanceof Error ? error.message : 'Unknown error';
      if (isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        if (data?.message) {
          message = data.message;
        }
      }
      showSnackbar(`Failed to generate document: ${message}`, 'error');
      setTenderForms((prev) =>
        prev.map((f) =>
          f.originalFilename === docObj.originalFilename ? { ...f, generationStatus: 'error' } : f
        )
      );
    } finally {
      const loadingKey = getLoadingKey(tender.id, 'generate', `form_${docObj.originalFilename}`);
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleDownloadFormInput = async (docObj: SupportDoc) => {
    const storageUri =
      docObj.storageUri || docObj.gcsPath || docObj.downloadURL || docObj.downloadUrl;
    if (storageUri) {
      handleDownload(
        storageUri,
        `form_input_${docObj.originalFilename}`,
        docObj.originalFilename || 'document'
      );
    }
  };

  const handleDownloadFilledForm = async (docObj: SupportDoc) => {
    const filledVersionStorageUri = docObj.filledVersionStorageUri || docObj.filledVersionGcsPath;
    if (filledVersionStorageUri) {
      const gcsExtMatch = filledVersionStorageUri.match(/\.([a-zA-Z0-9]+)$/);
      const gcsExt = gcsExtMatch ? `.${gcsExtMatch[1]}` : '';
      const baseName = (docObj.originalFilename || 'document').replace(/\.[^/.]+$/, '');
      handleDownload(
        filledVersionStorageUri,
        `form_export_${docObj.originalFilename}`,
        `${baseName}_Filled${gcsExt}`
      );
    }
  };

  return {
    handleGenerateAdHocDocument,
    handleDownloadFormInput,
    handleDownloadFilledForm,
  };
};
